import React from 'react';
import DOMPurify from 'dompurify';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/main.css';

const CONTENT = {
  es: {
    title: 'Términos de Servicio',
    lastUpdated: 'Última actualización: Marzo 2026',
    backToHome: 'Volver al inicio',
    sections: [
      {
        title: '1. Aceptación de Términos',
        content: `Al acceder y utilizar los servicios de IllumiDev, usted acepta estar sujeto a estos términos de servicio. Si no está de acuerdo con alguna parte de estos términos, no podrá utilizar nuestros servicios.`
      },
      {
        title: '2. Descripción de Servicios',
        content: `IllumiDev proporciona servicios de desarrollo web, automatización con IA y consultoría tecnológica. Los servicios específicos se detallan en cada propuesta o contrato individual.

Nuestros servicios incluyen pero no se limitan a:
• Desarrollo de sitios web y aplicaciones
• Automatización de procesos con IA
• Diseño UI/UX
• Consultoría tecnológica
• Mantenimiento y soporte`
      },
      {
        title: '3. Proceso de Trabajo',
        content: `**Propuesta**: Tras nuestra consulta inicial, proporcionaremos una propuesta detallada con alcance, timeline y precio.

**Aprobación**: El trabajo comienza una vez que apruebe la propuesta y realice el pago inicial acordado.

**Desarrollo**: Seguimos un proceso iterativo con puntos de revisión definidos.

**Entrega**: Los entregables se proporcionan según el calendario acordado.

**Revisiones**: Se incluye un número específico de revisiones según el paquete contratado.`
      },
      {
        title: '4. Pagos',
        content: `• Los términos de pago se especifican en cada propuesta
• Métodos aceptados: transferencia bancaria, PayPal, Stripe
• Los pagos tardíos pueden incurrir en cargos adicionales
• El trabajo puede pausarse por falta de pago
• Todos los precios están en USD a menos que se especifique lo contrario`
      },
      {
        title: '5. Propiedad Intelectual',
        content: `**Antes del pago completo**: IllumiDev retiene todos los derechos sobre el trabajo realizado.

**Después del pago completo**: La propiedad intelectual se transfiere al cliente, excepto:
• Código de terceros sujeto a sus propias licencias
• Herramientas y frameworks propietarios de IllumiDev
• Recursos con licencia de stock

El cliente es responsable de asegurar que tiene los derechos sobre el contenido proporcionado.`
      },
      {
        title: '6. Confidencialidad',
        content: `Ambas partes acuerdan mantener confidencial cualquier información propietaria compartida durante el proyecto. Esta obligación continúa después de la terminación del proyecto.`
      },
      {
        title: '7. Garantías y Soporte',
        content: `• Ofrecemos 30 días de soporte para corrección de bugs después de la entrega
• Las solicitudes de nuevas funcionalidades están fuera del alcance original
• El soporte extendido está disponible mediante contrato de mantenimiento
• No garantizamos resultados específicos de negocio o rankings de SEO`
      },
      {
        title: '8. Limitación de Responsabilidad',
        content: `IllumiDev no será responsable por:
• Daños indirectos, incidentales o consecuentes
• Pérdida de beneficios o datos
• Interrupciones del negocio

Nuestra responsabilidad máxima está limitada al monto pagado por los servicios.`
      },
      {
        title: '9. Terminación',
        content: `Cualquiera de las partes puede terminar el proyecto con aviso por escrito. En caso de terminación:
• El cliente paga por el trabajo completado hasta la fecha
• IllumiDev entrega el trabajo completado y pagado
• Los depósitos no son reembolsables a menos que se especifique lo contrario`
      },
      {
        title: '10. Cambios a los Términos',
        content: `Nos reservamos el derecho de modificar estos términos. Los cambios significativos se notificarán a los clientes activos.`
      },
      {
        title: '11. Contacto',
        content: `Para preguntas sobre estos términos:

**IllumiDev**
Email: legal@illumidev.com
Website: illumidev.com`
      }
    ]
  },
  en: {
    title: 'Terms of Service',
    lastUpdated: 'Last updated: March 2026',
    backToHome: 'Back to home',
    sections: [
      {
        title: '1. Acceptance of Terms',
        content: `By accessing and using IllumiDev services, you agree to be bound by these terms of service. If you do not agree with any part of these terms, you may not use our services.`
      },
      {
        title: '2. Description of Services',
        content: `IllumiDev provides web development, AI automation, and technology consulting services. Specific services are detailed in each individual proposal or contract.

Our services include but are not limited to:
• Website and application development
• AI process automation
• UI/UX design
• Technology consulting
• Maintenance and support`
      },
      {
        title: '3. Work Process',
        content: `**Proposal**: After our initial consultation, we will provide a detailed proposal with scope, timeline, and price.

**Approval**: Work begins once you approve the proposal and make the agreed initial payment.

**Development**: We follow an iterative process with defined review points.

**Delivery**: Deliverables are provided according to the agreed schedule.

**Revisions**: A specific number of revisions are included according to the contracted package.`
      },
      {
        title: '4. Payments',
        content: `• Payment terms are specified in each proposal
• Accepted methods: bank transfer, PayPal, Stripe
• Late payments may incur additional charges
• Work may be paused for non-payment
• All prices are in USD unless otherwise specified`
      },
      {
        title: '5. Intellectual Property',
        content: `**Before full payment**: IllumiDev retains all rights to work performed.

**After full payment**: Intellectual property is transferred to the client, except:
• Third-party code subject to its own licenses
• IllumiDev proprietary tools and frameworks
• Stock licensed resources

The client is responsible for ensuring they have rights to provided content.`
      },
      {
        title: '6. Confidentiality',
        content: `Both parties agree to keep confidential any proprietary information shared during the project. This obligation continues after project termination.`
      },
      {
        title: '7. Warranties and Support',
        content: `• We offer 30 days of bug-fix support after delivery
• New feature requests are outside the original scope
• Extended support is available through maintenance contract
• We do not guarantee specific business results or SEO rankings`
      },
      {
        title: '8. Limitation of Liability',
        content: `IllumiDev will not be liable for:
• Indirect, incidental, or consequential damages
• Loss of profits or data
• Business interruptions

Our maximum liability is limited to the amount paid for services.`
      },
      {
        title: '9. Termination',
        content: `Either party may terminate the project with written notice. In case of termination:
• Client pays for work completed to date
• IllumiDev delivers completed and paid work
• Deposits are non-refundable unless otherwise specified`
      },
      {
        title: '10. Changes to Terms',
        content: `We reserve the right to modify these terms. Significant changes will be notified to active clients.`
      },
      {
        title: '11. Contact',
        content: `For questions about these terms:

**IllumiDev**
Email: legal@illumidev.com
Website: illumidev.com`
      }
    ]
  }
};

const TermsPage = () => {
  const { language } = useLanguage();
  const t = CONTENT[language];

  return (
    <>
      <Header />
      <main className="legal-page">
        <div className="container">
          <Link to="/" className="back-link">
            <ArrowLeft size={18} /> {t.backToHome}
          </Link>
          
          <div className="legal-header">
            <FileText size={40} className="legal-icon" />
            <h1>{t.title}</h1>
            <p className="legal-updated">{t.lastUpdated}</p>
          </div>
          
          <div className="legal-content">
            {t.sections.map((section, i) => (
              <section key={i} className="legal-section">
                <h2>{section.title}</h2>
                <div className="legal-text">
                  {section.content.split('\n').map((line, j) => (
                    <p key={j} dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(line
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/• /g, '&bull; '))
                    }} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default TermsPage;
